/**
 * Deal Triage + Decision Escalation hooks (CoK Phase 2, v0).
 *
 * TanStack Query over the /growth-os/deal-triage/* endpoints. The config query
 * is always safe to call; the data queries 404 when the flag is off, so they
 * are gated on config.enabled by the caller.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/api/client';
import type {
  DealTriageConfig,
  TriageQueueResponse,
  DecisionsResponse,
  MetricsResponse,
  OrgTriageResponse,
  SuggestResponse,
} from '@/api/deal-triage-types';

const BASE = '/growth-os/deal-triage';

export const dealTriageKeys = {
  config: ['dealTriage', 'config'] as const,
  queue: (rule?: string, priority?: string, owner?: string) =>
    ['dealTriage', 'queue', rule, priority, owner] as const,
  decisions: ['dealTriage', 'decisions'] as const,
  metrics: ['dealTriage', 'metrics'] as const,
  org: (id: string) => ['dealTriage', 'org', id] as const,
};

/** Flag state — always safe to call; drives whether the surface renders. */
export function useDealTriageConfig() {
  return useQuery({
    queryKey: dealTriageKeys.config,
    queryFn: () => get<DealTriageConfig>(`${BASE}/config`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDealTriageEnabled(): boolean {
  const { data } = useDealTriageConfig();
  return data?.enabled ?? false;
}

export function useTriageQueue(
  rule?: string,
  priority?: string,
  owner?: string
) {
  return useQuery({
    queryKey: dealTriageKeys.queue(rule, priority, owner),
    queryFn: () => {
      const params = new URLSearchParams();
      if (rule) params.set('rule', rule);
      if (priority) params.set('priority', priority);
      if (owner) params.set('owner', owner);
      const qs = params.toString();
      return get<TriageQueueResponse>(`${BASE}/queue${qs ? `?${qs}` : ''}`);
    },
  });
}

export function useTriageDecisions() {
  return useQuery({
    queryKey: dealTriageKeys.decisions,
    queryFn: () => get<DecisionsResponse>(`${BASE}/decisions`),
  });
}

export function useTriageMetrics() {
  return useQuery({
    queryKey: dealTriageKeys.metrics,
    queryFn: () => get<MetricsResponse>(`${BASE}/metrics`),
  });
}

export function useOrgTriage(orgId: string, enabled = true) {
  return useQuery({
    queryKey: dealTriageKeys.org(orgId),
    queryFn: () => get<OrgTriageResponse>(`${BASE}/orgs/${orgId}`),
    enabled: enabled && !!orgId,
  });
}

/**
 * Turn an engine recommendation into a PENDING OrgSuggestion. This never
 * writes the org field — acceptance happens through the existing suggestion
 * review flow. Invalidates the org's suggestion + triage queries.
 */
export function useSuggestField(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { field: string; value: string }) =>
      post<SuggestResponse>(`${BASE}/orgs/${orgId}/suggest`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealTriageKeys.org(orgId) });
      qc.invalidateQueries({ queryKey: ['growthOS', 'suggestions', orgId] });
      qc.invalidateQueries({ queryKey: dealTriageKeys.queue() });
    },
  });
}
